import { useCallback, useMemo, useRef, useState } from "react";

import { useMutation, useQuery } from "convex/react";

import type { RelayIntent } from "@domain/intent-relay";
import {
  deriveRelayDisplayStatus,
  type RelayStatus,
} from "@domain/intent-relays-status";
import {
  taskCaptureUnavailable,
  taskRelayFailed,
  taskRelayPodUnavailable,
  taskRelayUnconfirmed,
} from "@domain/user-errors";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

/**
 * The single WRITE surface for the task manager (praxis-intent-relay
 * §3.7). Read surfaces (`useTasks`/`useTask`) stay untouched — this hook
 * owns the three relay mutations plus the §4.12 pending-relay
 * convention:
 *
 * Two pending signals, truth wins:
 *   A — durable `intentRelays.listActiveForCurrentUser` rows (survive
 *       reload; cleared when the server computes `satisfied`, i.e. the
 *       projection actually reflects the intent — never optimistically).
 *   B — unsettled mutation promises keyed by the hook-minted
 *       `clientRequestId`, covering the click→row gap.
 *
 * Failed rows map to inline error copy per affordance (no page banner).
 * Staleness (> 3 min unconfirmed) is derived AT RENDER via
 * `deriveRelayDisplayStatus` — no client timers (frontend-doctrine §8);
 * the chip surfaces on the next natural render.
 */

export type TaskWriteKind = "complete" | "resolve";

export interface PendingCapture {
  readonly relayId: string;
  readonly text: string;
  readonly requestedAt: number;
}

/** The subset of the relay-row shape the join consumes. */
export interface RelayRowLike {
  readonly relayId: string;
  readonly clientRequestId: string;
  readonly intent: RelayIntent;
  readonly targetBeadId?: string | undefined;
  readonly status: RelayStatus;
  readonly failureReason?: string | undefined;
  readonly requestedAt: number;
  readonly payload: string;
  readonly satisfied: boolean;
}

/** A signal-B entry: a mutation in flight, not yet visible as a row. */
export interface LocalWrite {
  readonly clientRequestId: string;
  readonly intent: RelayIntent;
  readonly taskId?: string | undefined;
  readonly text?: string | undefined;
  readonly requestedAt: number;
}

export interface JoinedWrites {
  readonly pendingByTaskId: ReadonlyMap<string, TaskWriteKind>;
  readonly pendingCaptures: readonly PendingCapture[];
  readonly errorByTaskId: ReadonlyMap<string, string>;
  readonly captureError: string | null;
}

// Spec-targeted intents (pause/activate-workflow) map to null — they
// never render on the task surface; useWorkflowPauseToggle owns them.
const KIND_BY_INTENT: Readonly<Record<RelayIntent, TaskWriteKind | null>> = {
  "complete-task": "complete",
  "resolve-bead": "resolve",
  "capture-task": null,
  "pause-workflow": null,
  "activate-workflow": null,
};

// Transport-skip reasons that mean "your pod is offline", mapped to the
// softer copy; everything else gets the generic failure line.
const POD_OFFLINE_REASONS = new Set([
  "pod_unavailable",
  "env_missing",
  "timeout",
]);

function failureCopy(reason: string | undefined): string {
  return reason !== undefined && POD_OFFLINE_REASONS.has(reason)
    ? taskRelayPodUnavailable()
    : taskRelayFailed();
}

/**
 * Pure join of the two pending signals into render-ready maps.
 * Dedupe: a local write whose clientRequestId already has a durable row
 * is dropped (the row wins). Projection wins: `satisfied` rows are
 * excluded outright. Pending wins over failure per task: a failed row
 * is only surfaced as an error when no newer pending write covers the
 * same task. Stale pending rows keep their pending mark AND gain the
 * unconfirmed chip copy.
 */
export function joinPendingWrites({
  relays,
  locals,
  dismissed,
  now,
}: {
  relays: readonly RelayRowLike[];
  locals: readonly LocalWrite[];
  dismissed: ReadonlySet<string>;
  now: number;
}): JoinedWrites {
  const pendingByTaskId = new Map<string, TaskWriteKind>();
  const pendingCaptures: PendingCapture[] = [];
  const errorByTaskId = new Map<string, string>();
  let captureError: string | null = null;
  let latestPendingCaptureAt = Number.NEGATIVE_INFINITY;
  let latestFailedCaptureAt = Number.NEGATIVE_INFINITY;

  const durableRequestIds = new Set(relays.map((r) => r.clientRequestId));

  // Pass 1 — pending (durable rows, then surviving locals).
  for (const relay of relays) {
    if (relay.satisfied || relay.status === "failed") continue;
    if (relay.intent === "capture-task") {
      pendingCaptures.push({
        relayId: relay.relayId,
        text: relay.payload,
        requestedAt: relay.requestedAt,
      });
      latestPendingCaptureAt = Math.max(
        latestPendingCaptureAt,
        relay.requestedAt,
      );
      continue;
    }
    const kind = KIND_BY_INTENT[relay.intent];
    if (kind !== null && relay.targetBeadId !== undefined) {
      pendingByTaskId.set(relay.targetBeadId, kind);
      if (deriveRelayDisplayStatus({ relay, now }) === "stale") {
        errorByTaskId.set(relay.targetBeadId, taskRelayUnconfirmed());
      }
    }
  }
  for (const local of locals) {
    if (durableRequestIds.has(local.clientRequestId)) continue;
    if (local.intent === "capture-task") {
      pendingCaptures.push({
        relayId: `relay_${local.clientRequestId}`,
        text: local.text ?? "",
        requestedAt: local.requestedAt,
      });
      latestPendingCaptureAt = Math.max(
        latestPendingCaptureAt,
        local.requestedAt,
      );
      continue;
    }
    const kind = KIND_BY_INTENT[local.intent];
    if (kind !== null && local.taskId !== undefined) {
      if (!pendingByTaskId.has(local.taskId)) {
        pendingByTaskId.set(local.taskId, kind);
      }
    }
  }

  // Pass 2 — failures (suppressed when a newer pending write covers the
  // same affordance, or the user dismissed them).
  for (const relay of relays) {
    if (relay.satisfied || relay.status !== "failed") continue;
    if (dismissed.has(relay.relayId)) continue;
    if (relay.intent === "capture-task") {
      if (
        relay.requestedAt > latestPendingCaptureAt &&
        relay.requestedAt > latestFailedCaptureAt
      ) {
        latestFailedCaptureAt = relay.requestedAt;
        captureError = taskCaptureUnavailable();
      }
      continue;
    }
    if (
      relay.targetBeadId !== undefined &&
      !pendingByTaskId.has(relay.targetBeadId)
    ) {
      errorByTaskId.set(relay.targetBeadId, failureCopy(relay.failureReason));
    }
  }

  pendingCaptures.sort((a, b) => a.requestedAt - b.requestedAt);
  return { pendingByTaskId, pendingCaptures, errorByTaskId, captureError };
}

export interface UseTaskWritesResult extends JoinedWrites {
  completeTask(args: { taskId: string }): Promise<void>;
  captureTask(args: { text: string }): Promise<void>;
  resolveDecision(args: { taskId: string; answer: string }): Promise<void>;
  clearErrors(): void;
}

export function useTaskWrites(): UseTaskWritesResult {
  const relays = useQuery(api.intentRelays.listActiveForCurrentUser, {});
  const requestComplete = useMutation(api.intentRelays.requestCompleteTask);
  const requestCapture = useMutation(api.intentRelays.requestCaptureTask);
  const requestResolve = useMutation(api.intentRelays.requestResolveDecision);

  const [locals, setLocals] = useState<readonly LocalWrite[]>([]);
  const [throwErrorByTaskId, setThrowErrorByTaskId] = useState<
    ReadonlyMap<string, string>
  >(new Map());
  const [captureThrowError, setCaptureThrowError] = useState<string | null>(
    null,
  );
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  // Rendered relayIds, kept for clearErrors to dismiss durable failures.
  const failedRelayIdsRef = useRef<readonly string[]>([]);

  const addLocal = useCallback((entry: LocalWrite) => {
    setLocals((prev) => [...prev, entry]);
  }, []);
  const removeLocal = useCallback((clientRequestId: string) => {
    setLocals((prev) =>
      prev.filter((l) => l.clientRequestId !== clientRequestId),
    );
  }, []);

  const runRelay = useCallback(
    async (
      entry: LocalWrite,
      send: () => Promise<unknown>,
      onError: () => void,
      opts?: { rethrow?: boolean },
    ): Promise<void> => {
      addLocal(entry);
      try {
        await send();
      } catch (err) {
        // INVALID_STATE = the row was stale (already done / resolved);
        // truth is about to refresh the UI — no error chip for that.
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.includes("INVALID_STATE")) {
          onError();
          // Capture's draft-survives-failure contract needs the
          // rejection to propagate (the component always catches).
          if (opts?.rethrow === true) throw err;
        }
      } finally {
        removeLocal(entry.clientRequestId);
      }
    },
    [addLocal, removeLocal],
  );

  const completeTask = useCallback(
    async ({ taskId }: { taskId: string }): Promise<void> => {
      const clientRequestId = crypto.randomUUID();
      await runRelay(
        {
          clientRequestId,
          intent: "complete-task",
          taskId,
          requestedAt: Date.now(),
        },
        () =>
          requestComplete({ beadId: taskId as Id<"beads">, clientRequestId }),
        () =>
          setThrowErrorByTaskId((prev) =>
            new Map(prev).set(taskId, taskRelayFailed()),
          ),
      );
    },
    [requestComplete, runRelay],
  );

  const captureTask = useCallback(
    async ({ text }: { text: string }): Promise<void> => {
      const clientRequestId = crypto.randomUUID();
      setCaptureThrowError(null);
      await runRelay(
        {
          clientRequestId,
          intent: "capture-task",
          text,
          requestedAt: Date.now(),
        },
        () => requestCapture({ text, clientRequestId }),
        () => setCaptureThrowError(taskCaptureUnavailable()),
        { rethrow: true },
      );
    },
    [requestCapture, runRelay],
  );

  const resolveDecision = useCallback(
    async ({
      taskId,
      answer,
    }: {
      taskId: string;
      answer: string;
    }): Promise<void> => {
      const clientRequestId = crypto.randomUUID();
      await runRelay(
        {
          clientRequestId,
          intent: "resolve-bead",
          taskId,
          requestedAt: Date.now(),
        },
        () =>
          requestResolve({
            beadId: taskId as Id<"beads">,
            answer,
            clientRequestId,
          }),
        () =>
          setThrowErrorByTaskId((prev) =>
            new Map(prev).set(taskId, taskRelayFailed()),
          ),
      );
    },
    [requestResolve, runRelay],
  );

  const joined = useMemo(
    () =>
      joinPendingWrites({
        relays: relays ?? [],
        locals,
        dismissed,
        now: Date.now(),
      }),
    [relays, locals, dismissed],
  );

  failedRelayIdsRef.current = (relays ?? [])
    .filter((r) => r.status === "failed")
    .map((r) => r.relayId);

  const clearErrors = useCallback(() => {
    setThrowErrorByTaskId(new Map());
    setCaptureThrowError(null);
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const id of failedRelayIdsRef.current) next.add(id);
      return next;
    });
  }, []);

  // Mutation-throw errors overlay the durable-row errors (throws are
  // fresher — they reflect the attempt the user just made).
  const errorByTaskId = useMemo(() => {
    if (throwErrorByTaskId.size === 0) return joined.errorByTaskId;
    const merged = new Map(joined.errorByTaskId);
    for (const [taskId, copy] of throwErrorByTaskId) merged.set(taskId, copy);
    return merged;
  }, [joined.errorByTaskId, throwErrorByTaskId]);

  return {
    completeTask,
    captureTask,
    resolveDecision,
    pendingByTaskId: joined.pendingByTaskId,
    pendingCaptures: joined.pendingCaptures,
    errorByTaskId,
    captureError: captureThrowError ?? joined.captureError,
    clearErrors,
  };
}
